import dayjs from 'dayjs';
import { useChatContext } from '@/contexts/chat/context';
import type { ChatMessage } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import type { Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import { serverTime } from '@/contexts/chat/scripts/hooks/useSystemTime/utils';
import { formatDuration } from '@/scripts/utils';
import { ParticleEffectEnum } from '@/components/particle-effect-item';
import { defaultMsgExpireDuration } from '@/configs/options';
import { useRefs } from '.';

export function useSetInterval() {
  const [time, setTime] = useState<number>(Infinity);
  const timestampRef = useRef(Infinity);
  const startRef = useRef(0);

  const run = useCallback((timestamp: number, cb?: () => void) => {
    setTime(timestamp);

    if (timestamp !== Infinity) {
      timestampRef.current = timestamp;
      startRef.current = serverTime.time;

      const intervalTimerId = window.setInterval(() => {
        const remaining = timestampRef.current - (serverTime.time - startRef.current);

        if (remaining <= 1000) {
          window.clearInterval(intervalTimerId);
          setTime(0);

          const timerId = setTimeout(() => {
            cb?.();
            clearTimeout(timerId);
          }, 0);
        } else {
          setTime(remaining);
        }
      }, 1000);
    }
  }, []);

  return [time, run] as const;
}

export default function useMessageExpireTimer({
  message,
  chat,
  doExpireAnimation = () => Promise.resolve(ParticleEffectEnum.Duration),
  onExpire,
}: {
  message: ChatMessage;
  chat: Chat;
  doExpireAnimation: () => Promise<ParticleEffectEnum.Duration>;
  onExpire?: () => void;
}) {
  const [time, run] = useSetInterval();

  const {
    deleteExpiredMessagesByIds,
    fileMessages: { expireFile },
  } = useChatContext();

  const timestamp = useMemo(
    () =>
      message.read_at
        ? message.expire_duration - (serverTime.time - dayjs(message.read_at).valueOf())
        : Infinity,
    [message.expire_duration, message.read_at]
  );

  const refs = useRefs({
    chat,
    deleteExpiredMessagesByIds,
    onExpire,
    doExpireAnimation,
  });

  const displayTime = useMemo(() => {
    return formatDuration(
      time === Infinity ? message.expire_duration || defaultMsgExpireDuration : time
    );
  }, [message.expire_duration, time]);

  useLayoutEffect(() => {
    run(timestamp, async () => {
      const { deleteExpiredMessagesByIds, chat, onExpire, doExpireAnimation } =
        refs.current;
      await doExpireAnimation();
      await deleteExpiredMessagesByIds(chat.chat_id, [message.id]);
      await expireFile(message.cli_msg_id);
      onExpire?.();
    });
  }, [run, timestamp, message.id, refs, expireFile, message.cli_msg_id]);

  return { displayTime };
}
